import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tent } from "lucide-react";

function useInvite(token) {
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(token ? "" : "This invitation link is missing its token.");
  useEffect(() => {
    if (!token) return;
    api.get(`/auth/invite/${encodeURIComponent(token)}`)
      .then((r) => setInvite(r.data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail)));
  }, [token]);
  return { invite, error, setError };
}

function PasswordForm({ onSubmit, busy }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(password, confirm); }} className="space-y-4">
      <div className="space-y-1.5">
        <Label>Password (min 8 characters)</Label>
        <Input type="password" data-testid="invite-password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={8} required className="min-h-[44px]" />
      </div>
      <div className="space-y-1.5">
        <Label>Confirm password</Label>
        <Input type="password" data-testid="invite-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className="min-h-[44px]" />
      </div>
      <Button type="submit" data-testid="invite-submit" className="w-full min-h-[44px]" disabled={busy}>{busy ? "Activating…" : "Activate account"}</Button>
    </form>
  );
}

export default function AcceptInvite() {
  const token = useSearchParams()[0].get("token") || "";
  const nav = useNavigate();
  const { setUser } = useAuth();
  const { invite, error, setError } = useInvite(token);
  const [busy, setBusy] = useState(false);

  const activate = async (password, confirm) => {
    if (password !== confirm) { setError("Passwords do not match"); return; }
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/invite/accept", { token, password });
      setUser(data);
      nav("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <Card className="w-full max-w-md card-shadow-soft" data-testid="accept-invite-card">
        <CardHeader>
          <div className="w-11 h-11 rounded-lg bg-primary text-primary-foreground flex items-center justify-center mb-3"><Tent className="w-5 h-5" /></div>
          <CardTitle className="text-2xl" style={{ fontFamily: "Outfit" }}>Welcome to Lough Scouts</CardTitle>
          {invite && <p className="text-sm text-muted-foreground" data-testid="invite-greeting">Hi {invite.name} — set a password for <strong>{invite.email}</strong> to activate your {invite.role} account.</p>}
        </CardHeader>
        <CardContent>
          {error && <div data-testid="invite-error" className="text-sm text-destructive bg-destructive/10 p-2.5 rounded-md mb-4">{error}</div>}
          {invite && <PasswordForm onSubmit={activate} busy={busy} />}
          {!invite && !error && <p className="text-sm text-muted-foreground">Checking your invitation…</p>}
          {!invite && error && (
            <Button variant="outline" className="w-full min-h-[44px] mt-2" data-testid="invite-back-login" onClick={() => nav("/login")}>Back to sign in</Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
